import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import type { LessonWordGroup } from '@/types'
import { wordsApi } from '@/api/words'
import { getErrorMessage } from '@/utils/error'
import {
  isLessonWordGroupIncomplete,
  lessonWordOriginal,
  lessonWordTranscription,
  lessonWordTranslation,
} from './lessonWordUtils'

type LessonWordListProps = {
  items: LessonWordGroup[]
  canDelete: boolean
  canEdit: boolean
  deletingWordId: number | null
  showWordDetails?: boolean
  emptyMessage?: string
  onDelete: (item: LessonWordGroup) => void
  onEdited: () => Promise<void>
}

export function LessonWordList({
  items,
  canDelete,
  canEdit,
  deletingWordId,
  showWordDetails = true,
  emptyMessage,
  onDelete,
  onEdited,
}: LessonWordListProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<LessonWordGroup | null>(null)
  const [text, setText] = useState('')
  const [translation, setTranslation] = useState('')
  const [transcription, setTranscription] = useState('')
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  const openEdit = (item: LessonWordGroup) => {
    setEditing(item)
    setText(lessonWordOriginal(item.word))
    setTranslation(lessonWordTranslation(item.word))
    setTranscription(lessonWordTranscription(item.word))
    setEditError(null)
  }

  const closeEdit = () => {
    if (saving) return
    setEditing(null)
    setEditError(null)
  }

  const handleSave = async () => {
    if (!editing) return
    setSaving(true)
    setEditError(null)
    try {
      await wordsApi.updateWord(editing.word_id, {
        word: text,
        translation,
        transcription,
      })
      setEditing(null)
      await onEdited()
    } catch (err) {
      setEditError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(emptyMessage ?? 'lessonWords.empty', 'Слова на этом уроке ещё не добавлены.')}
      </Typography>
    )
  }

  return (
    <>
      <List dense disablePadding>
        {items.map((item) => {
          const original = lessonWordOriginal(item.word)
          const translationText = showWordDetails ? lessonWordTranslation(item.word) : ''
          const transcriptionText = showWordDetails ? lessonWordTranscription(item.word) : ''
          const incomplete = showWordDetails && isLessonWordGroupIncomplete(item)

          return (
            <ListItem
              key={item.word_id}
              disableGutters
              secondaryAction={
                canDelete || canEdit ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {canEdit && (
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={t('lessonWords.edit', 'Редактировать')}
                        onClick={() => openEdit(item)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={t('lessonWords.delete', 'Удалить')}
                        disabled={deletingWordId === item.word_id}
                        onClick={() => onDelete(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : undefined
              }
              sx={{ alignItems: 'flex-start', py: 0.75, pr: canDelete || canEdit ? 10 : undefined }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    <Typography component="span" fontWeight={600}>
                      {original}
                    </Typography>
                    {incomplete && (
                      <Chip
                        size="small"
                        label={t('lessonWords.incomplete', 'Не заполнено')}
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  translationText || transcriptionText ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                      {translationText && (
                        <Typography variant="caption" color="text.secondary">
                          {t('lessonWords.translationLabel', 'Перевод')}: {translationText}
                        </Typography>
                      )}
                      {transcriptionText && (
                        <Typography variant="caption" color="text.secondary">
                          {t('lessonWords.transcriptionLabel', 'Транскрипция')}: {transcriptionText}
                        </Typography>
                      )}
                    </Box>
                  ) : null
                }
              />
            </ListItem>
          )
        })}
      </List>

      <Dialog open={editing !== null} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{t('lessonWords.editTitle', 'Редактировать слово')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {editError && (
            <Typography color="error" variant="body2">
              {editError}
            </Typography>
          )}
          <TextField
            label={t('lessonWords.wordLabel', 'Слово')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
            fullWidth
            disabled={saving}
          />
          <TextField
            label={t('lessonWords.translationLabel', 'Перевод')}
            value={translation}
            onChange={(e) => setTranslation(e.target.value)}
            size="small"
            fullWidth
            disabled={saving}
          />
          <TextField
            label={t('lessonWords.transcriptionLabel', 'Транскрипция')}
            value={transcription}
            onChange={(e) => setTranscription(e.target.value)}
            size="small"
            fullWidth
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !text.trim()}>
            {saving ? t('common.loading') : t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
